import { Injectable } from '@angular/core';
import { ProductSectionItemViewModel } from '../product-section-item/product-section-item-view-model';
import { combineLatest, map, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { SectionRowViewModel } from '../../menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { Cannabinoid, PrimaryCannabinoid } from '../../../../../../../../models/enum/shared/cannabinoid';
import { exists } from '../../../../../../../../functions/exists';
import { SortUtils } from '../../../../../../../../utils/sort-utils';
import { EnumUtils } from '../../../../../../../../utils/enum-utils';
import { Terpene } from '../../../../../../../../models/enum/shared/terpene';
import type { SectionRowViewModelSizeAndQuantity } from '../../menu-section/product-section/section-row-view-models/section-row-view-model-size-and-quantity';
import type { Variant } from '../../../../../../../../models/product/dto/variant';

type PropertiesFromPool = { title: string; value: string };

@Injectable()
export class ProductSectionItemExpandedProductInfoViewModel extends ProductSectionItemViewModel {
  public readonly cannabinoidPropertiesToTake = 2;
  public readonly terpenesPropertiesToTake = 0;

  public override rowViewModel$: Observable<SectionRowViewModelSizeAndQuantity>;

  public readonly cannabinoidPropertiesBasedOnPools$ = this.rowViewModel$.pipe(
    map(data => {
      if (this.cannabinoidPropertiesToTake <= 0) return [];
      return this.getCannabinoidProperties(data, this.cannabinoidPropertiesToTake);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasCannabinoidProperties$ = this.cannabinoidPropertiesBasedOnPools$.pipe(
    map(properties => properties?.length > 0)
  );

  public readonly terpenePropertiesBasedOnPools$ = this.rowViewModel$.pipe(
    map(data => {
      if (this.terpenesPropertiesToTake <= 0) return [];
      return this.getTerpeneProperties(data, this.terpenesPropertiesToTake);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasTerpeneProperties$ = this.terpenePropertiesBasedOnPools$.pipe(
    map(properties => properties?.length > 0)
  );

  public readonly totalAccumulatedTerpenes$ = this.rowViewModel$.pipe(
    map(vm => vm?.getTotalActiveTerpenes()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly topTerpene$ = this.rowViewModel$.pipe(
    map(vm => vm?.getTopTerpene()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly showTerpsAndTopTerpene$ = combineLatest([this.totalAccumulatedTerpenes$, this.topTerpene$]).pipe(
    map(([totalAccumulatedTerpenes, topTerpene]) => {
      return this.propertyAsStringExists(totalAccumulatedTerpenes) && this.propertyAsStringExists(topTerpene);
    })
  );

  protected getCannabinoidProperties(data: SectionRowViewModel, propertiesToTake: number): PropertiesFromPool[] {
    if (data?.isNonCannabinoidVariant()) return [];
    return this.getPropertiesFromPool(data, propertiesToTake, this.cannabinoidsToUseForPoolOfProductProperties(data));
  }

  protected getTerpeneProperties(data: SectionRowViewModel, propertiesToTake: number): PropertiesFromPool[] {
    if (data?.isNonCannabinoidVariant()) return [];
    return this.getPropertiesFromPool(data, propertiesToTake, this.terpenesToUseForPoolOfProductProperties(data));
  }

  protected cannabinoidsToUseForPoolOfProductProperties(data: SectionRowViewModel): Cannabinoid[] {
    return [
      PrimaryCannabinoid.CBD,
      PrimaryCannabinoid.THC,
      ...(data?.companyConfig?.enabledSecondaryCannabinoids || [])
    ];
  }

  protected terpenesToUseForPoolOfProductProperties(data: SectionRowViewModel): string[] {
    return data?.companyConfig?.enabledTerpenes || [];
  }

  protected propertyAsStringExists(value: string): boolean {
    return exists(value) && value !== '-' && value !== '--';
  }

  protected getPoolOfProductPropertiesFilterLambda(): (a: PropertiesFromPool) => boolean {
    return (a: PropertiesFromPool) => this.propertyAsStringExists(a?.value);
  }

  protected getPoolOfProductPropertiesSortLambda(): (a: PropertiesFromPool, b: PropertiesFromPool) => number {
    return (a: PropertiesFromPool, b: PropertiesFromPool) => {
      return SortUtils.parseFloatFromStringDescending(a?.value, b?.value);
    };
  }

  protected getPropertiesFromPool(
    data: SectionRowViewModel,
    numberToTake: number,
    cannabinoidOrTerpenePropertiesToLookThrough: string[]
  ): PropertiesFromPool[] {
    if (numberToTake <= 0) return [];
    let properties: PropertiesFromPool[] = [];
    const allCannabinoidKeys = EnumUtils.getCannabinoids();
    const allTerpeneKeys = EnumUtils.getTerpenes();
    cannabinoidOrTerpenePropertiesToLookThrough?.forEach(cannabinoidOrTerpene => {
      if (allCannabinoidKeys.includes(cannabinoidOrTerpene as Cannabinoid)) {
        properties.push({ title: cannabinoidOrTerpene, value: data?.getCannabinoid(cannabinoidOrTerpene) });
      } else if (allTerpeneKeys.includes(cannabinoidOrTerpene as Terpene)) {
        properties.push({ title: cannabinoidOrTerpene, value: data?.getTerpene(cannabinoidOrTerpene) });
      }
    });
    properties = properties
      ?.filter(this.getPoolOfProductPropertiesFilterLambda())
      ?.sort(this.getPoolOfProductPropertiesSortLambda());
    return properties.take(numberToTake);
  }

  public getExpandedGridSize$(
    variant: Variant,
    removeSpacing: boolean = true,
    upperCaseUnits: boolean = true
  ): Observable<string> {
    return this.rowViewModel$.pipe(
      map(rowViewModel => this.getExpandedGridSizeHelper(rowViewModel, variant, removeSpacing, upperCaseUnits))
    );
  }

  private getExpandedGridSizeHelper(
    rowViewModel: SectionRowViewModelSizeAndQuantity,
    variant: Variant,
    removeSpacing: boolean = true,
    upperCaseUnits: boolean = true
  ): string {
    const size = rowViewModel?.getGridQuantityAndSizeString(variant);
    const split = size?.split(' ');
    const lastIndex = split?.length - 1;
    if (lastIndex > -1 && upperCaseUnits) split[lastIndex] = split[lastIndex]?.toUpperCase();
    const splitLowerCased = split?.map(it => it?.toLowerCase());
    if (splitLowerCased?.includes('caps') || splitLowerCased?.includes('cap')) return split?.join(' ');
    return split?.join(removeSpacing ? '' : ' ');
  }
}
